import { useState } from 'react';
import { Grid, Typography, Box, IconButton, Paper, Chip, Collapse } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function RaportitList({ raportit, handleDeleteRaportti, fetchData, onSuccess, onError }) {
    const [selectedRaportti, setSelectedRaportti] = useState(null);
    const [newRaporttiData, setNewRaporttiData] = useState({ tapahtumaId: null, muistiinpano: '', yhteistyotahot: [], laskut: [], user: null, vakimaara: null });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [searchTerm, setSearchTerm] = useState('');

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1); // Reset to the first page when searching
    };

    // Apply the search filter
    const filteredRaportit = raportit?.filter((Raportti) =>
        Raportti.tapahtumaId.toString().includes(searchTerm.toLowerCase())
    ) || [];

    const totalPages = Math.ceil(filteredRaportit.length / itemsPerPage);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedRaportit = filteredRaportit.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };



    const handleUserClick = (Raportti) => {
        if (selectedRaportti?.id === Raportti.id) {
            setSelectedRaportti(null);
            setNewRaporttiData({ tapahtumaId: null, muistiinpano: '', yhteistyotahot: [], laskut: [], user: null, vakimaara: null });
            return;
        }
        setSelectedRaportti(Raportti);
        setNewRaporttiData(Raportti);
    };


    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Raportit
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {displayedRaportit.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei raportteja näytettäväksi.
                    </Typography>
                ) : (
                    displayedRaportit.map((Raportti) => {
                        const isSelected = selectedRaportti?.id === Raportti.id;
                        const notePreview = Raportti.muistiinpano?.replace(/\s+/g, ' ').trim();

                        return (
                            <Box key={Raportti.id} sx={{ mb: 1.5 }}>
                                <Box
                                    onClick={() => handleUserClick(Raportti)}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            Tapahtuma {Raportti.tapahtumaId}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {notePreview ? `${notePreview.slice(0, 120)}${notePreview.length > 120 ? '...' : ''}` : 'Ei muistiinpanoja'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            <Chip size="small" label={`${Raportti.yhteistyotahot?.length || 0} yhteistyötahoa`} />
                                            <Chip size="small" variant="outlined" label={`${Raportti.laskut?.length || 0} laskua`} />
                                            {Raportti.vakimaara !== null && Raportti.vakimaara !== undefined && (
                                                <Chip size="small" variant="outlined" label={`Väkimäärä ${Raportti.vakimaara}`} />
                                            )}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            handleDeleteRaportti(event, Raportti);
                                            if (selectedRaportti?.id === Raportti.id) {
                                                setSelectedRaportti(null);
                                                setNewRaporttiData({ tapahtumaId: null, muistiinpano: '', yhteistyotahot: [], laskut: [], user: null, vakimaara: null });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Raportin tiedot
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="tapahtumaId"
                                                    label="Tapahtuma ID"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={String(newRaporttiData.tapahtumaId || '')}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="vakimaara"
                                                    label="Väkimäärä"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={String(newRaporttiData.vakimaara || '')}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormTextField
                                                    fieldName="muistiinpano"
                                                    label="Muistiinpano"
                                                    multiline={true}
                                                    rows={6}
                                                    fullWidth={true}
                                                    value={newRaporttiData.muistiinpano || ''}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="yhteistyotahot"
                                                    label="Yhteistyötahot"
                                                    multiline={true}
                                                    rows={6}
                                                    fullWidth={true}
                                                    value={(newRaporttiData.yhteistyotahot || []).map((item) => `${item.nimi} (${item.suhtautuminen})`).join('\n')}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="laskut"
                                                    label="Laskut"
                                                    multiline={true}
                                                    rows={6}
                                                    fullWidth={true}
                                                    value={(newRaporttiData.laskut || []).map((item) => `${item.maksava_taho || 'Tuntematon'}: ${item.summa || 0} EUR`).join('\n')}
                                                    disabled
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {filteredRaportit.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredRaportit}
                            array={raportit}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default RaportitList;